import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000BasicDialog from "../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ComboBox from "../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000InputCalendarCustom from "../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumEditMode } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import { NS } from "../../U3130";
import type { U3130SommairePaiePayrollPeriodOption } from "../../U3130TimesheetModel";
import type { U3130ExportDepensesParSemaineParCodePdfRequest, U3130ExportEmployeurDRequest, U3130ExportSommairePaiePdfRequest, U3130PayrollPeriodReportExportRequest } from "../../U3130Service";
import { u3130PickDefaultPayrollPeriodForReport } from "../../tools/u3130ActivePayrollPeriodReport";
import { u3130IsValidCivilDateRange } from "../../tools/u3130ReportDateRange";
import { u2000CurrentWeekMondayYmd, u2000ShiftCivilYmd } from "../../../u2000Core/tools/U2000DateTimeHelpers";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";

export interface U3130SommairePaieReportFormRow extends Record<string, unknown> {
  payrollPeriodId: string;
  startDate: string;
  endDate: string;
}

function getDefaultWeekRange(): { startDate: string; endDate: string } {
  const monday = u2000CurrentWeekMondayYmd();
  const sunday = u2000ShiftCivilYmd(monday, 6) ?? monday;
  return { startDate: monday, endDate: sunday };
}

type U3130ReportPrintMode = "sommairePaie" | "depensesParSemaineParCode" | "employeurDExcel";

interface U3130SommairePaieReportDialogProps {
  open: boolean;
  onClose: () => void;
  payrollPeriodOptions: U3130SommairePaiePayrollPeriodOption[];
  reportLang: "fr" | "en";
  onSubmitSommairePaie: (body: U3130ExportSommairePaiePdfRequest) => void | Promise<void>;
  onSubmitDepensesParSemaineParCode: (body: U3130ExportDepensesParSemaineParCodePdfRequest) => void | Promise<void>;
  onSubmitEmployeurD: (body: U3130ExportEmployeurDRequest) => void | Promise<void>;
  isExportingSommairePaie: boolean;
  isExportingDepensesParSemaineParCode: boolean;
  isExportingEmployeurD: boolean;
}

const U3130SommairePaieReportDialog: React.FC<U3130SommairePaieReportDialogProps> = ({
  open,
  onClose,
  payrollPeriodOptions,
  reportLang,
  onSubmitSommairePaie,
  onSubmitDepensesParSemaineParCode,
  onSubmitEmployeurD,
  isExportingSommairePaie,
  isExportingDepensesParSemaineParCode,
  isExportingEmployeurD,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const isExporting = isExportingSommairePaie || isExportingDepensesParSemaineParCode || isExportingEmployeurD;
  const [reportMode, setReportMode] = useState<U3130ReportPrintMode>("sommairePaie");
  const [form, setForm] = useState<U3130SommairePaieReportFormRow>(() => {
    const w = getDefaultWeekRange();
    return { payrollPeriodId: "", startDate: w.startDate, endDate: w.endDate };
  });

  useEffect(() => {
    if (!open) return;
    if (payrollPeriodOptions.length > 0) {
      const def = u3130PickDefaultPayrollPeriodForReport(payrollPeriodOptions);
      if (def) {
        setForm({
          payrollPeriodId: def.id,
          startDate: def.startAt.trim().slice(0, 10),
          endDate: def.endAt.trim().slice(0, 10),
        });
        return;
      }
    }
    const w = getDefaultWeekRange();
    setForm({ payrollPeriodId: "", startDate: w.startDate, endDate: w.endDate });
  }, [open, payrollPeriodOptions]);

  const periodRows = useMemo(
    () =>
      payrollPeriodOptions.map((o) => ({
        id: o.id,
        label: o.label,
        startAt: o.startAt,
        endAt: o.endAt,
        yearCode: o.yearCode,
        periodNumber: o.periodNumber,
      })),
    [payrollPeriodOptions],
  );

  const noop = useCallback(() => {}, []);

  const formStateHandlers = useMemo((): U2000FormStateHandlers<U3130SommairePaieReportFormRow> => {
    const normDate = (v: unknown) => (v != null && String(v).trim() !== "" ? String(v).trim().slice(0, 10) : "");
    return {
      getData: () => ({ ...form }),
      setData: (row) => {
        setForm((prev) => {
          const next = { ...(row as U3130SommairePaieReportFormRow) };
          const prevStart = normDate(prev.startDate);
          const prevEnd = normDate(prev.endDate);
          const nextStart = normDate(next.startDate);
          const nextEnd = normDate(next.endDate);
          if ((nextStart !== prevStart || nextEnd !== prevEnd) && prev.payrollPeriodId) {
            next.payrollPeriodId = "";
          }
          return next;
        });
      },
      getTouched: () => ({}),
      markTouched: () => {},
      getErrors: () => ({}),
      isSubmitted: () => false,
      getFormState: () => "EDIT",
      setEditMode: noop,
      saveData: noop,
      getOldData: () => null,
      afterSaveData: noop,
      cancelData: noop,
      getFormEditMode: () => U2000FormStateEnumEditMode.ADD,
      setErrors: noop,
      setOtherErrors: noop,
      getOtherErrors: () => ({}),
      setSubmitted: noop,
      isValid: () => true,
      getCustomAttributes: () => ({}),
      setCustomAttributes: noop,
      onRecordChange: noop,
      onPageChange: noop,
      onLeave: noop,
      getBusinessHandlers: () => ({}),
      onBlur: noop,
      onChange: (e, value) => {
        const id = e.target.id;
        if (id === "payrollPeriodId") {
          const pid = value == null || value === "" ? "" : String(value);
          setForm((prev) => {
            const opt = periodRows.find((o) => o.id === pid);
            if (opt) {
              return {
                ...prev,
                payrollPeriodId: pid,
                startDate: opt.startAt.slice(0, 10),
                endDate: opt.endAt.slice(0, 10),
              };
            }
            return { ...prev, payrollPeriodId: pid };
          });
          return;
        }
        setForm((prev) => ({ ...prev, [id]: value }));
      },
    };
  }, [form, noop, periodRows]);

  const handleClose = (reason: string) => {
    void reason;
    onClose();
  };

  const buildPeriodExportBody = (start: string, end: string): U3130PayrollPeriodReportExportRequest => {
    const body: U3130PayrollPeriodReportExportRequest = {
      lang: reportLang,
      startDate: start.slice(0, 10),
      endDate: end.slice(0, 10),
    };
    const pid = form.payrollPeriodId?.trim();
    const selected = pid ? payrollPeriodOptions.find((o) => o.id === pid) : undefined;
    const label = selected?.label?.trim();
    if (label) {
      body.payrollPeriodLabel = label;
    }
    return body;
  };

  const handlePrint = () => {
    const start = form.startDate?.trim() ?? "";
    const end = form.endDate?.trim() ?? "";
    if (!start || !end) {
      return;
    }
    if (!u3130IsValidCivilDateRange(start, end)) {
      toast.error(t("msgReportSommaireInvalidRange", { ns: NS }), t("errorTitle", { ns: NS }));
      return;
    }
    if (reportMode === "depensesParSemaineParCode") {
      void onSubmitDepensesParSemaineParCode(buildPeriodExportBody(start, end));
      return;
    }

    if (reportMode === "employeurDExcel") {
      void onSubmitEmployeurD(buildPeriodExportBody(start, end));
      return;
    }

    void onSubmitSommairePaie(buildPeriodExportBody(start, end));
  };

  const actionButtonLabel =
    reportMode === "employeurDExcel" ? t("btnExportEmployeurD", { ns: NS }) : t("btnPrint", { ns: NS });

  const hasPeriodCombo = periodRows.length > 0;

  return (
    <U2000BasicDialog
      isOpen={open}
      onClose={handleClose}
      title={t("dlgReportPrintTitle", { ns: NS })}
      width="w-[520px]"
      contentOverflowVisible
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        <fieldset className="flex flex-col gap-2 border-0 p-0 m-0">
          <legend className="sr-only">{t("dlgReportPrintTitle", { ns: NS })}</legend>
          <label className={`flex items-center gap-2 text-sm ${isExporting ? "cursor-not-allowed text-gray-500" : "cursor-pointer text-gray-800"}`}>
            <input
              type="radio"
              name="u3130-report-type"
              checked={reportMode === "sommairePaie"}
              disabled={isExporting}
              onChange={() => setReportMode("sommairePaie")}
              className="h-4 w-4 border-gray-300 text-blue-600"
            />
            {t("reportTypeSommairePaie", { ns: NS })}
          </label>
          <label className={`flex items-center gap-2 text-sm ${isExporting ? "cursor-not-allowed text-gray-500" : "cursor-pointer text-gray-800"}`}>
            <input
              type="radio"
              name="u3130-report-type"
              checked={reportMode === "depensesParSemaineParCode"}
              disabled={isExporting}
              onChange={() => setReportMode("depensesParSemaineParCode")}
              className="h-4 w-4 border-gray-300 text-blue-600"
            />
            {t("reportTypeExpensesByWeekCode", { ns: NS })}
          </label>
          <label className={`flex items-center gap-2 text-sm ${isExporting ? "cursor-not-allowed text-gray-500" : "cursor-pointer text-gray-800"}`}>
            <input
              type="radio"
              name="u3130-report-type"
              checked={reportMode === "employeurDExcel"}
              disabled={isExporting}
              onChange={() => setReportMode("employeurDExcel")}
              className="h-4 w-4 border-gray-300 text-blue-600"
            />
            {t("reportTypeEmployeurD", { ns: NS })}
          </label>
        </fieldset>

        {reportMode === "employeurDExcel" ? (
          <p className="text-sm text-gray-600">
            <a
              href="/assets/docs/u3130/index.html#employeur-d"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 underline hover:text-blue-900"
            >
              {t("lnkEmployeurDImportGuide", { ns: NS })}
            </a>
          </p>
        ) : null}

        <div className="flex flex-col gap-3">
          {hasPeriodCombo ? (
            <div className="min-w-0 w-full">
              <U2000ComboBox<{ id: string; label: string; startAt: string; endAt: string; yearCode?: string; periodNumber?: number }>
                formStateHandlers={formStateHandlers}
                fieldName="payrollPeriodId"
                label={t("lblPayrollPeriodForReport", { ns: NS })}
                data={periodRows}
                keyField="id"
                descField="label"
                getValue={(item) => item.id}
                formatLabel={(item) => item.label}
                findItemByValue={(value, data) => data.find((d) => String(d.id) === String(value)) ?? null}
                placeholder={t("placeholderPayrollPeriodReport", { ns: NS })}
                disabled={isExporting}
              />
            </div>
          ) : null}
          <div className="flex w-full min-w-0 flex-row flex-nowrap items-end gap-3">
            <div className="min-w-0 flex-1">
              <U2000InputCalendarCustom
                formStateHandlers={formStateHandlers}
                fieldName="startDate"
                label={t("lblReportStartDate", { ns: NS })}
                dateFormat="yyyy-MM-dd"
                readonly={isExporting}
                popupZIndex={9999}
              />
            </div>
            <div className="min-w-0 flex-1">
              <U2000InputCalendarCustom
                formStateHandlers={formStateHandlers}
                fieldName="endDate"
                label={t("lblReportEndDate", { ns: NS })}
                dateFormat="yyyy-MM-dd"
                readonly={isExporting}
                popupZIndex={9999}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
          <U2000ActionBtn
            label={t("btnCancel", { ns: "common", defaultValue: "Annuler" })}
            handleClick={() => onClose()}
            disabled={isExporting}
          />
          <U2000ActionBtn
            label={actionButtonLabel}
            handleClick={() => handlePrint()}
            disabled={isExporting || !form.startDate?.trim() || !form.endDate?.trim()}
            className="bg-blue-700! text-white! hover:bg-blue-800!"
          />
        </div>
      </div>
    </U2000BasicDialog>
  );
};

export default U3130SommairePaieReportDialog;
